import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Table,
  Card,
  Tag,
  Modal,
  Button,
  Space,
  Typography,
  Empty,
  Grid,
  List,
  Descriptions
} from 'antd';
import {
  ShoppingCartOutlined,
  EyeOutlined,
  ShoppingOutlined
} from '@ant-design/icons';
import SEO from "../../components/seo";
import LayoutOne from "../../layouts/LayoutOne";
import Breadcrumb from "../../wrappers/breadcrumb/Breadcrumb";
import { getOrders } from "../../store/actions/order";
import { getCountries } from "../../store/actions/country";
import { getCities } from "../../store/actions/city";
import { getStates } from "../../store/actions/state";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const orderStatusColors = {
  0: 'gold',    // PENDING
  1: 'blue',    // IN-PROCESS
  2: 'red',     // CANCEL
  3: 'green',   // COMPLETE
};

const orderStatusMap = {
  0: 'PENDING',
  1: 'IN-PROCESS',
  2: 'CANCEL',
  3: 'COMPLETE',
};

const CheckOrders = () => {
  // const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  const handleShowModal = (orderAddress) => {
    try {
      const parsed = JSON.parse(orderAddress);
      setSelectedAddress(parsed);
      setIsModalVisible(true);
    } catch {
      setSelectedAddress(null);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedAddress(null);
  };
  const [selectedImage, setSelectedImage] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let { pathname } = useLocation();
  const screens = useBreakpoint();
  const countryList = useSelector((state) => state.country.list);
  const stateList = useSelector((state) => state.state.list);
  const cityList = useSelector((state) => state.city.list);

  // const isLoggedIn = useSelector((state) => state.user.user !== null);
  const isLoggedIn = localStorage.getItem("userauth");
  const orders = useSelector((state) => state.order.list);

  useEffect(() => {
    dispatch(getCountries());
    dispatch(getCities())
    dispatch(getStates())

  }, [dispatch]);
  useEffect(() => {
    const fetchOrders = async () => {
      if (!isLoggedIn) {
        navigate("/login-register");
        return;
      }
      await dispatch(getOrders());
    };
    fetchOrders();
  }, [dispatch, isLoggedIn, navigate]);

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsModalVisible(true);
  };

  // Mobile order item renderer
  const renderOrderItem = (item) => {
    const discountedPrice = item.disPer
      ? item.price - (item.price * item.disPer / 100)
      : item.price;

    return (
      <Card
        size="small"
        style={{ marginBottom: 8 }}
        bodyStyle={{ padding: '12px' }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Link >
            <Text strong>{item.name}</Text>
          </Link>
          <Space split={<Text type="secondary" style={{ margin: '0 8px' }}>|</Text>}>
            <Text>Qty: {item.qty}</Text>
            <Text>
              ₹{discountedPrice}
              {item.disPer > 0 && (
                <Text type="secondary" style={{ marginLeft: 8, textDecoration: 'line-through' }}>
                  ₹{item.price}
                </Text>
              )}
            </Text>
          </Space>
          <Text>Subtotal: ₹{(discountedPrice * item.qty).toFixed(2)}</Text>
        </Space>
      </Card>
    );
  };

  // Mobile order renderer
  const renderMobileOrder = (order) => (
    <Card
      key={order._id}
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: screens.xs ? 12 : 24 }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space split={<Text type="secondary" style={{ margin: '0 8px' }}>|</Text>}>
          <Text copyable>{order._id}</Text>
          <Tag color={orderStatusColors[order.orderStatus]}>
            {orderStatusMap[order.orderStatus]}
          </Tag>
        </Space>

        <Descriptions
          column={1}
          size="small"
          labelStyle={{ fontWeight: 'bold' }}
          contentStyle={{ textAlign: 'right' }}
        >
          <Descriptions.Item label="Total Amount">₹{order.totalAmt}</Descriptions.Item>
          <Descriptions.Item label="Advance Payment">₹{order.advAmt}</Descriptions.Item>
          <Descriptions.Item label="Balance">₹{order.totalAmt - order.advAmt}</Descriptions.Item>
        </Descriptions>

        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleImageClick(order.imageUrl)}
          style={{ padding: 0 }}
        >
          View Payment Proof
        </Button>

        <div style={{ marginTop: 8 }}>
          <Text strong>Order Items:</Text>
          {order.items.map((item, index) => (
            <div key={index} style={{ marginTop: 8 }}>
              {renderOrderItem(item)}
            </div>
          ))}
        </div>
      </Space>
    </Card>
  );

  const AddressModalCell = ({ addressData, cityList, stateList, countryList }) => {
    const [open, setOpen] = useState(false);

    let address = {};
    try {
      address = JSON.parse(addressData);
    } catch (e) {
      return 'Invalid Address';
    }

    const cityName = cityList?.find(city => city._id === address.cityId)?.name || 'N/A';
    const stateName = stateList?.find(state => state._id === address.stateId)?.name || 'N/A';
    const countryName = countryList?.find(country => country._id === address.countryId)?.name || 'N/A';

    return (
      <>
        <Button size="small" onClick={() => setOpen(true)}>Show</Button>
        <Modal
          open={open}
          onCancel={() => setOpen(false)}
          footer={null}
          title="Full Address"
        >
          <p><strong>Address:</strong> {address.address}</p>
          <p><strong>City:</strong> {cityName}</p>
          <p><strong>State:</strong> {stateName}</p>
          <p><strong>Pincode:</strong> {address.pinCode}</p>
          <p><strong>Country:</strong> {countryName}</p>
          <p><strong>Mobile:</strong> {address.mobile}</p>
        </Modal>
      </>
    );
  };
  // Desktop table columns
  const columns = [
    {
      title: 'Order ID',
      dataIndex: '_id',
      key: '_id',
      render: (id) => <Text copyable>{id}</Text>,
      ellipsis: true,
    },
    {
      title: 'Payment ID',
      dataIndex: 'payment',
      key: 'razorpayOrderId',
      render: (payment) => (
        <Text>{payment?.razorpayOrderId || 'N/A'}</Text>
      ),
      ellipsis: true,
    },
    {
      title: ' Status',
      dataIndex: 'payment',
      key: 'paymentStatus',
      render: (payment) => <Text >{payment?.paymentStatus || 'N/A'}</Text>,
      ellipsis: true,
    },

    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => {
        if (!createdAt) return 'N/A';

        const date = new Date(createdAt);
        const day = date.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12; // Convert to 12-hour format

        const formattedDate = `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;

        return <Text>{formattedDate}</Text>;
      },
      ellipsis: true,
    },

    {
      title: 'Status',
      dataIndex: 'orderStatus',
      key: 'status',
      render: (status) => (
        <Tag color={orderStatusColors[status]}>
          {orderStatusMap[status]}
        </Tag>
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmt',
      key: 'totalAmt',
      render: (_, record) => {
        const total = record.items?.reduce((acc, item) => {
          const itemPrice = item.price ?? 0;
          return acc + itemPrice;
        }, 0);

        const formattedAmount = Math.floor(total); // remove last 2 digits
        return `₹${formattedAmount.toLocaleString()}`;
      },
    },

    {
      title: 'Coupon Payment',
      dataIndex: 'couponAmt',
      key: 'couponAmt',
      render: (amount) => `₹${amount}`,
      responsive: ['md'],
    },
    {
      title: 'Delivery Charge',
      dataIndex: 'deliveryCharge',
      key: 'deliveryCharge',
      render: (amount) => `₹${amount}`,
      responsive: ['md'],
    },
    {
      title: 'Total Payable',
      key: 'balance',
      render: (_, record) => {
        // Calculate total from items
        const total = record.items?.reduce((acc, item) => {
          const itemPrice = item.price ?? 0;
          return acc + itemPrice;
        }, 0) || 0;

        // Remove last 2 digits
        const totalWithoutPaise = total
        const advanceWithoutPaise = Math.floor((record.advAmt || 0) / 100);

        // Compute remaining balance
        const remaining = totalWithoutPaise + record.deliveryCharge

        return `₹${remaining.toLocaleString()}`;
      },
      responsive: ['md'],
    },
    {
      title: 'Advance Payment',
      dataIndex: 'advAmt',
      key: 'advAmt',
      render: (amount) => {
        const cleanAmount = Math.floor(amount / 100); // removes last 2 digits
        return `₹${cleanAmount.toLocaleString()}`;
      },
      responsive: ['md'],
    },
    {
      title: 'Balance',
      key: 'balance',
      render: (_, record) => {
        // Calculate total from items
        const total = record.items?.reduce((acc, item) => {
          const itemPrice = item.price ?? 0;
          return acc + itemPrice;
        }, 0) || 0;

        // Remove last 2 digits
        const totalWithoutPaise = total
        const advanceWithoutPaise = Math.floor((record.advAmt || 0) / 100);

        // Compute remaining balance
        const remaining = advanceWithoutPaise > 0
          ? totalWithoutPaise - advanceWithoutPaise + record.deliveryCharge
          : totalWithoutPaise;

        return `₹${remaining.toLocaleString()}`;
      },
      responsive: ['md'],
    },

    {
      title: 'Address',
      dataIndex: 'orderAddress',
      key: 'orderAddress',
      render: (orderAddress) => (
        <AddressModalCell
          addressData={orderAddress}
          cityList={cityList}
          stateList={stateList}
          countryList={countryList}
        />
      )
    }




  ];
  // /to={`${ process.env.PUBLIC_URL } /product/${ record._id } `}
  // Expandable row for desktop
  console.log(orders, "orders")
  const expandedRowRender = (order) => {
    const columns = [
      {
        title: 'Product Name',
        dataIndex: 'name',
        key: 'name',
        render: (name, record) => {
          return (
            <Link >
              {name}
            </Link>
          );
        }
      },
      {
        title: 'Imei No',
        dataIndex: 'imei',
        key: 'imei',
        render: (name, record) => {
          return (
            <Link >
              {name}
            </Link>
          );
        }
      },
      {
        title: 'Price',
        dataIndex: 'price',
        key: 'price',
        render: (price, record) => {
          const discountedPrice = record.price
            ? price - (price * record.price / 100)
            : price;

          return (
            <div>
              <Text strong style={{ marginRight: 8 }}>
                ₹{(record.price)}
              </Text>
              {record.disPer > 0 && (
                <Text type="secondary" style={{ textDecoration: 'line-through' }}>
                  ₹{record.mrp.toLocaleString()}
                </Text>
              )}
            </div>
          );
        },
      },
      {
        title: 'Quantity',
        dataIndex: 'qty',
        key: 'qty',
      },
      {
        title: 'Subtotal',
        key: 'subtotal',
        render: (_, record) => {

          return `₹${(record.price)} `;
        },
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={order.items}
        pagination={false}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          fontFamily: '"Inter", "Segoe UI", "Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
          border: '1px solid #e8ecf4'
        }}
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{
                  backgroundColor: '#f8fafc',
                  color: '#1e293b',
                  fontWeight: '600',
                  fontSize: '14px',
                  borderBottom: '2px solid #e2e8f0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontFamily: '"Inter", "Segoe UI", "Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
                  ...props.style
                }}
              />
            )
          },
          body: {
            row: (props) => (
              <tr
                {...props}
                style={{
                  backgroundColor: '#ffffff',
                  transition: 'all 0.2s ease-in-out',
                  borderBottom: '1px solid #f1f5f9',
                  ':hover': {
                    backgroundColor: '#f8fafc',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                  },
                  ...props.style
                }}

              />
            ),
            cell: (props) => (
              <td
                {...props}
                style={{
                  padding: '16px 20px',
                  color: '#334155',
                  fontSize: '15px',
                  fontWeight: '400',
                  lineHeight: '1.5',
                  fontFamily: '"Inter", "Segoe UI", "Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
                  borderBottom: '1px solid #f1f5f9',
                  ...props.style
                }}
              />
            )
          }
        }}
        className="custom-table"
      />


    );
  };

  return (
    <Fragment>
      <SEO titleTemplate="CheckOrders" description="Check Orders of 09Mobile Gallery." />
      <LayoutOne headerTop="visible">
        <Breadcrumb
          pages={[
            { label: "Home", path: process.env.PUBLIC_URL + "/" },
            { label: "Orders", path: process.env.PUBLIC_URL + pathname },
          ]}
        />

        <div className="cart-main-area  pb-100">
          <div className="container">
            {orders && orders.length > 0 ? (
              <Fragment>
                <Card>
                  <Title level={3} style={{ marginBottom: 24 }}>Your ordered items</Title>

                  {/* Responsive Layout Switch */}
                  {screens.md ? (
                    // Desktop View
                    <Table
                      columns={columns}
                      dataSource={orders}
                      expandable={{
                        expandedRowRender,
                        expandRowByClick: true,
                      }}
                      rowKey="_id"
                      scroll={{ x: true }}
                      style={{
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ffa726 50%, #42a5f5 100%)',
                        borderRadius: '20px',
                        boxShadow: '0 25px 50px rgba(255, 107, 107, 0.3)',
                        overflow: 'hidden',
                        fontFamily: '"Nunito", "Poppins", sans-serif',
                        border: 'none',
                        backdropFilter: 'blur(10px)'
                      }}
                      components={{
                        header: {
                          cell: (props) => (
                            <th
                              {...props}
                              style={{
                                background: 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)',
                                color: '#ffffff',
                                fontWeight: '800',
                                fontSize: '14px',
                                padding: '5px 5px',
                                borderBottom: '4px solid #fbbf24',
                                textTransform: 'uppercase',
                                letterSpacing: '1.2px',
                                fontFamily: '"Nunito", "Poppins", sans-serif',
                                textShadow: '0 2px 4px rgba(0, 0, 0, 0.6)',
                                textAlign: "center",
                                ...props.style
                              }}
                            />
                          )
                        },
                        body: {
                          row: (props) => (
                            <tr
                              {...props}
                              style={{
                                background: 'rgba(255, 255, 255, 0.95)',
                                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                                backdropFilter: 'blur(10px)',
                                ...props.style
                              }}
                            />
                          ),
                          cell: (props) => (
                            <td
                              {...props}
                              style={{
                                color: '#1f2937',
                                fontSize: '16px',
                                fontWeight: '600',
                                lineHeight: '0.3',
                                fontFamily: '"Nunito", "Poppins", sans-serif',
                                transition: 'all 0.3s ease',
                                textAlign: "justify",
                                borderBottom: '1px solid black',
                                borderRight: "1px solid gray",
                                ...props.style
                              }}
                            />
                          )
                        }
                      }}
                      className="vibrant-colorful-table"
                    />
                  ) : (
                    // Mobile View
                    <List
                      dataSource={orders}
                      renderItem={renderMobileOrder}
                      pagination={false}
                    />
                  )}

                  {/* <div style={{ 
                    textAlign: 'right', 
                    marginTop: 24,
                    padding: screens.xs ? '0 12px' : 0 
                  }}>
                    <Link to={`${ process.env.PUBLIC_URL }/shop-grid-standard`
}>
  <Button type="primary" icon={<ShoppingOutlined />}>
    Continue Shopping
  </Button>
                    </Link >
                  </div > */}
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="cart-shiping-update-wrapper">
                        <div className="cart-shiping-update">
                          <Link to={`${process.env.PUBLIC_URL}/shop-grid-standard`}>Continue Shopping</Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card >
              </Fragment >
            ) : (
              <Card>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Space direction="vertical" align="center">
                      <Text>No items found in orders</Text>
                      <Link to={`${process.env.PUBLIC_URL}/shop-grid-standard`}>
                        <Button type="primary" icon={<ShoppingCartOutlined />}>
                          Shop Now
                        </Button>
                      </Link>
                    </Space>
                  }
                />
              </Card>
            )}
          </div >
        </div >

        <Modal
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={screens.md ? 800 : '95%'}
          title="Payment Proof"
        >
          <img
            src={selectedImage}
            alt="Payment Proof"
            style={{ width: '100%' }}
          />
        </Modal>
        <style jsx>{`
  .custom-table .ant-table-thead > tr > th {
    background-color: #f8fafc !important;
    color: #1e293b !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    border-bottom: 2px solid #e2e8f0 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    font-family: "Inter", "Segoe UI", "Roboto", -apple-system, BlinkMacSystemFont, sans-serif !important;
  }

  .custom-table .ant-table-tbody > tr > td {
    color: #334155 !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    line-height: 1.5 !important;
    font-family: "Inter", "Segoe UI", "Roboto", -apple-system, BlinkMacSystemFont, sans-serif !important;
    border-bottom: 1px solidrgb(23, 122, 221) !important;
  }

  .custom-table .ant-table-tbody > tr:hover > td {
    background-color: #f8fafc !important;
  }

  .custom-table .ant-table-tbody > tr:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
  }

  .custom-table .ant-table-container {
    border-radius: 12px !important;
  }

  .custom-table .ant-table {
    border-radius: 12px !important;
  }

  .custom-table .ant-table-thead > tr:first-child > th:first-child {
    border-top-left-radius: 12px !important;
  }

  .custom-table .ant-table-thead > tr:first-child > th:last-child {
    border-top-right-radius: 12px !important;
  }

  .custom-table .ant-table-tbody > tr:last-child > td:first-child {
    border-bottom-left-radius: 12px !important;
  }

  .custom-table .ant-table-tbody > tr:last-child > td:last-child {
    border-bottom-right-radius: 12px !important;
  }
`}</style>
      </LayoutOne >
    </Fragment >
  );
};

export default CheckOrders;

